import { useEffect } from 'react';

export const MESSAGE_TYPES = {
  NAVIGATE_TO_UNIT: 'navigateToUnit',
} as const;

type MessageType = typeof MESSAGE_TYPES;
type MessageTypeValue = MessageType[keyof MessageType];

interface MessageDataMap {
  [MESSAGE_TYPES.NAVIGATE_TO_UNIT]: {
    courseId: string;
    prereqSectionName: string;
    prereqId: string;
    sequenceTitle: string;
  };
}

type MessageHandler<T extends MessageTypeValue> = (data: MessageDataMap[T]) => void;

type HandlerConfig = {
  [K in MessageTypeValue]?: MessageHandler<K>;
};

/**
 * A generic message bus for handling window.postMessage events in a type-safe way.
 *
 * @example
 * // Basic usage
 * useMessageBus({
 *   [MESSAGE_TYPES.NAVIGATE_TO_UNIT]: (data) => {
 *     // data is properly typed as MessageDataMap[typeof MESSAGE_TYPES.NAVIGATE_TO_UNIT]
 *     console.log('Navigating to unit:', data.prereqId);
 *   },
 *   // Add more message handlers as needed
 * });
 */
export function useMessageBus(handlers: HandlerConfig) {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, data } = event.data || {};

      if (!type || typeof type !== 'string') {
        return;
      }

      const handler = handlers[type as keyof typeof handlers];
      if (handler) {
        handler(data);
      } else if (process.env.NODE_ENV !== 'production') {
        console.warn(`No handler registered for message type: ${type}`); // eslint-disable-line no-console
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handlers]);
}

type NavigateToUnitHandler = MessageHandler<typeof MESSAGE_TYPES.NAVIGATE_TO_UNIT>;

export function useNavigateToUnit(handler: NavigateToUnitHandler) {
  useMessageBus({
    [MESSAGE_TYPES.NAVIGATE_TO_UNIT]: handler,
  });
}
